import type { JSONSchema } from 'json-schema-typed';
import { ComputedProperties, ComputedPropertySchema } from '../ReplicantComputedProperties';
import { getIndexSchemaFromComputedProps } from '../ReplicantIndexes';
import type SB from '../SchemaBuilder';
import { schemaBuilderToJSONSchema } from './schemaBuilderToJSONSchema';
import { unreachable } from './Utils';

export function computedPropertiesToQuerySchema(computedProperties: ComputedProperties): Exclude<JSONSchema, boolean> {
    const whereSchema: JSONSchema = {
        ...objectBase,
        description: 'Indexing search/count API query object',
        properties: {
            id: {
                ...objectBase,
                properties: {
                    isOneOf: {
                        type: 'array',
                        items: { type: 'string' },
                    },
                    isNotOneOf: {
                        type: 'array',
                        items: { type: 'string' },
                    },
                },
            },
        },
    };

    const indexedProperties: { [key: string]: ComputedPropertySchema } =
        getIndexSchemaFromComputedProps(computedProperties);

    for (const key in indexedProperties) {
        whereSchema.properties![key] = computedPropertyToQuerySchema(indexedProperties[key]!);
    }

    return {
        $schema: 'http://json-schema.org/draft-07/schema#',

        definitions: {
            where: whereSchema,

            booleanFilter: {
                ...objectBase,
                properties: {
                    is: { type: 'boolean' },
                    isNot: { type: 'boolean' },
                },
            },

            numberFilter: {
                ...objectBase,
                properties: {
                    between: { type: 'array', items: { type: 'number' }, maxItems: 2 },
                    lessThan: { type: 'number' },
                    greaterThan: { type: 'number' },
                    lessThanOrEqual: { type: 'number' },
                    greaterThanOrEqual: { type: 'number' },
                },
            },

            integerFilter: {
                ...objectBase,
                properties: {
                    between: { type: 'array', items: { type: 'integer' }, maxItems: 2 },
                    lessThan: { type: 'integer' },
                    greaterThan: { type: 'integer' },
                    lessThanOrEqual: { type: 'integer' },
                    greaterThanOrEqual: { type: 'integer' },
                },
            },

            stringFilter: {
                ...objectBase,
                properties: {
                    isAnyOf: { type: 'array', items: { type: 'string' } },
                    isNoneOf: { type: 'array', items: { type: 'string' } },
                },
            },

            matchableStringFilter: {
                ...objectBase,
                properties: {
                    matchesAnyOf: { type: 'array', items: { type: 'string' } },
                },
            },
        },

        ...objectBase,
        properties: {
            where: { type: 'array', items: { $ref: '#/definitions/where' }, minItems: 1 },
        },
    };
}

function computedPropertyToQuerySchema(property: ComputedPropertySchema): JSONSchema {
    switch (property._type) {
        case 'boolean':
            return { $ref: '#/definitions/booleanFilter' };
        case 'number':
            return { $ref: '#/definitions/numberFilter' };
        case 'integer':
            return { $ref: '#/definitions/integerFilter' };
        case 'string':
            return property._extendedType === 'matchableString'
                ? { $ref: '#/definitions/matchableStringFilter' }
                : { $ref: '#/definitions/stringFilter' };
        case 'tuple': {
            const itemSchema = computedPropertyToPrimitiveSchema(property);
            return {
                ...objectBase,
                properties: {
                    isAnyOf: { type: 'array', items: itemSchema },
                    isNoneOf: { type: 'array', items: itemSchema },
                },
            };
        }
        case 'array': {
            const item = (property as SB.ArraySchema<any>).getItemSchema() as ComputedPropertySchema;
            if (item._type === 'object') {
                return computedPropertyToQuerySchema(item);
            } else {
                const itemsSchema = computedPropertyToPrimitiveSchema(item);
                return {
                    ...objectBase,
                    properties: {
                        containsAnyOf: { type: 'array', items: itemsSchema },
                        containsNoneOf: { type: 'array', items: itemsSchema },
                    },
                };
            }
        }
        case 'object': {
            const schema = { ...objectBase, properties: {} as { [key: string]: JSONSchema } };

            const subProperties = (property as SB.ObjectSchema<any>).getFullSchema();
            for (const key in subProperties) {
                const subPropertySchema = computedPropertyToQuerySchema(subProperties[key]);
                schema.properties[key] = subPropertySchema;
            }

            return schema;
        }
        default:
            unreachable(property._type);
    }
}

function computedPropertyToPrimitiveSchema(property: ComputedPropertySchema): JSONSchema {
    switch (property._type) {
        case 'boolean':
        case 'number':
        case 'integer':
        case 'string':
        case 'tuple':
            return schemaBuilderToJSONSchema(property);
        case 'array':
        case 'object':
            throw Error('deeply nested arrays/objects are not supported');
        default:
            unreachable(property._type);
    }
}

const objectBase = { type: 'object' as const, additionalProperties: false };
